import { Component, Input, OnChanges } from '@angular/core';
import { Subscription } from 'rxjs/Rx';
import { ComputeService } from '../services/compute.service';
import { TranslateService, TranslatePipe, LangChangeEvent } from '@ngx-translate/core';
import { GlobalVariables } from '../../../../../common/global-variables';
import { ActivatedRoute, Params } from '@angular/router';
import { AuthService } from '../../../../auth/auth.service';
import { ArrayUtils } from '../../../../../common/utils/array-utils';
import { storagePrettyConverter, setVcdRedirectUrlCookie } from '../../../../../common/utils/utils';
const _ = require('lodash');

import { RefreshSharedService } from '../../../../refresh-shared-service';

@Component({
  selector: 'vdc',
  providers: [
    ComputeService, TranslateService
  ],
  styles: [require('./vdc.scss')],
  template: require('./vdc.html')
})
export default class Vdc implements OnChanges {
  @Input('isVisible') isVisible: boolean;
  private jsonData: any;
  private loaded: boolean = false;
  private subscription: Subscription;
  private isAddVdcPopupVisible: boolean = false;
  private isEditVdcPopupVisible: boolean = false;
  private isDeleteWarningVisible: boolean = false;
  private isDeleteVdcPopupVisible: boolean = false;
  private vdcIdFromRoute: string;
  private isEmptyEntity: boolean = false;
  private vdcSSOURL: string;
  private isEditVdcDialogVisible: boolean = false;
  private isEditSPDialogVisible: boolean = false;
  private hasChildren: boolean;
  private currentClassName: string = '';
  private rsService: RefreshSharedService;
  private refreshSubscription: Subscription;
  private isDpsHealthy: boolean = false;
  private storageProfiles = [];

  constructor(public computeService: ComputeService,
              private translateService: TranslateService,
              public authService: AuthService,
              private route: ActivatedRoute,
              private rss: RefreshSharedService) {
    this.loaded = false;
    translateService.use(GlobalVariables.LOCALE);
    this.rsService = rss;
    this.currentClassName = 'Vdc';
  }

  ngOnInit() {
    this.refreshSubscription = this.rsService.getRefreshParam()
    .subscribe (
      item => {
        this.handleEmittedValues(item);
    });

    this.route.params.forEach((params: Params) => {
        this.vdcIdFromRoute = params['id2'];
        this.rsService.setParam({
            'loadedComponent': this.currentClassName,
            'detailPageIDName': params['id2'],
            'shouldReloadInventoryTree': true
          })
        this.getVdcDetails();
    });
  }

   handleEmittedValues(item) {
      if (item.loadedComponent === this.currentClassName && this.vdcIdFromRoute === item.detailPageIDName) {
          this.jsonData = [];
          this.getVdcDetails();
      }
  }


  ngOnChanges(changes) {
  }

  ngOnDestroy() {
    this.rsService.resetParam();
    if (this.refreshSubscription) {
        this.refreshSubscription.unsubscribe();
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onEditVdc(event) {
    this.isEditVdcDialogVisible = true;
  };

  onEditSP(event) {
    this.isEditSPDialogVisible = true;
  };

  onDeleteVdc = ($event) => {
    if (this.hasChildren) {
      this.isDeleteWarningVisible = true;
    } else {
      this.isDeleteVdcPopupVisible = true;
    }
  };

  onAddVdc = ($event) => {
    this.isAddVdcPopupVisible = true;
  };

  onDeleteVdcClose = ($event) => {
    if ($event && $event.value) {
      this.isDeleteVdcPopupVisible = false;
    }
  };

  onEditVdcDialogClose(event) {
    if (event && event.value) {
      this.isEditVdcDialogVisible = false;
    }
  }

  onSPDialogClose(event) {
    if (event && event.value) {
      this.isEditSPDialogVisible = false;
    }
  }

  orgVdcWizardStatusChange(event) {
    this.isEditVdcPopupVisible = event;
  }

  editVdcDialogStatusChange(event) {
    this.isEditVdcDialogVisible = event;
  }

  editSPDialogStatusChange(event) {
    this.isEditSPDialogVisible = event;
  }

  getVdcDetails() {
    if (this.vdcIdFromRoute && this.vdcIdFromRoute !== '') {
      this.loaded = false;
      this.subscription = this.computeService.getVdcs(this.vdcIdFromRoute)
        .subscribe(
          res => {
            // Hack to make the dev setup with JSON server work
            this.jsonData = ('production' === ENV) ? res[0] : (res && res[0] && res[0].result && res[0].result[0]) ? res[0].result[0] : [];
            this.loaded = true;
            this.hasChildren = this.jsonData.hasChildren;
            this.isDpsHealthy = (this.authService.DPS_HEALTH && this.authService.DPS_HEALTH.isHealthy) ? true : false;            
            this.isEmptyEntity = this.checkIfEmptyEntity(res);
            this.setVdcSSOURL(this.jsonData.orgName, this.vdcIdFromRoute);
          },
          error => this.logError(error, 'get vdc details'),
          () => this.logOperationCompleted('get vdc details')
        );
      this.getStorageProfiles(this.vdcIdFromRoute);
    } else {
      this.logError('Could not get vdc id from the route');
    }
  }

  logError(error, origin: String = '') {
    console.error('Error: ', error, (origin !== '') ? ' Error from ' + origin : '');
  }

  logOperationCompleted(msg: string) {
    console.log('Operation Completed: ', msg);
  }

  checkIfEmptyEntity(restResult): boolean {
    return (restResult && restResult.constructor === Array && restResult.length === 0) ? true : false;
  }

  getCpuUsage() {
    let x = _.map(this.jsonData, _.property('vcd.computeCapacity.cpu'));
    if (!ArrayUtils.isPropertyExist(this.jsonData, 'vcd.computeCapacity.cpu')) {
      return '-';
    }
    let data = this.jsonData.vcd.computeCapacity.cpu;
    return storagePrettyConverter( data.allocated, data.units );
  }

  getMemoryUsage() {
    if (!ArrayUtils.isPropertyExist(this.jsonData, 'vcd.computeCapacity.memory')) {
      return '-';
    }
    let data = this.jsonData.vcd.computeCapacity.memory;
    return storagePrettyConverter( data.allocated, data.units );
  }

  getStorageProfiles(vdcId: string) {
    this.computeService.getVirtualdc(vdcId).subscribe(
        (res: any) => {
          let storageProfiles = res.vdcStorageProfiles;
          this.storageProfiles = [];
          for(let storageProfile of storageProfiles) {
            let used = storageProfile.storageUsedMB;
            let limit = storageProfile.limit;
            let percentage;
            if (limit && limit !== 0) {
              percentage = ((used / limit) * 100).toFixed(2);
            }
            let usedWithUnits = storagePrettyConverter( used, "MB" );
            let limitWithUnits = storagePrettyConverter( limit, "MB" );
            this.storageProfiles.push({
              "name":storageProfile.name,
              "percentage": percentage,
              "usedWithUnits": usedWithUnits,
              "limitWithUnits": limitWithUnits
            });
          }
        });
  }

  isResourceRunningOut(usagePercent: number) {
    return usagePercent > 90;
  }

  getStorageUsage() {
    if (!ArrayUtils.isPropertyExist(this.jsonData, 'vcd.storageCapacity')) {
      return '-';
    }
    let data = this.jsonData.vcd.storageCapacity;
    return storagePrettyConverter( data.used, data.units );
  }

  getStorageReserved() {
    if (!ArrayUtils.isPropertyExist(this.jsonData, 'vcd.storageCapacity')) {
      return '-';
    }
    let data = this.jsonData.vcd.storageCapacity;
    return storagePrettyConverter( data.reserved, data.units );
  }

  setVdcSSOURL(orgName: string, vdcId: string) {
    if (!orgName || orgName === '') {
        return;
    }
    let url = document.createElement('a');
    url.href = window.location.href;
    this.vdcSSOURL = url.protocol + '//' + url.host + '/compute/cloud/org/' + orgName.toLowerCase()
        + '/#/OrgVdcVAppsList?orgVdc=' + vdcId;
  }

    onMangeInVcd = ($event) => {
        if (this.vdcSSOURL) {
            let orgName = this.jsonData.orgName;
            setVcdRedirectUrlCookie(orgName, this.vdcSSOURL);
            window.open(this.vdcSSOURL, '_blank');
        }
    }

};



// WEBPACK FOOTER //
// ./src/app/components/compute/compute-vcd/vdc/vdc.component.ts